import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import { generateClient } from 'aws-amplify/api';
import { format, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { Schema } from '../../../../../amplify/data/resource';
import { FundLogo } from '../../../../components/custom/FundLogo';
import ContributionsTable from '../../../../components/ContributionsTable/ContributionsTable';
import {
  calculateAge,
  extractFundNumber,
  formatMoney,
  formatSalaryMonth,
} from '../../../../utils/format/format';

const sidebarVariants = {
  hidden: { x: '-100%', transition: { duration: 0.5 } },
  visible: { x: 0, transition: { duration: 0.5 } },
};

// @ts-ignore
type Employee = Schema['Employee']['type'];
// @ts-ignore
type Deposit = Schema['Deposit']['type'];

const apiClient = generateClient<Schema>();

const monthsInHebrew = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

interface EmployeeSidebarProps {
  open: boolean;
  onClose: () => void;
  employeeId: string;
}

const EmployeeSidebar: React.FC<EmployeeSidebarProps> = ({
  open,
  onClose,
  employeeId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const fileId = searchParams.get('fileId');

  const date = searchParams.get('date') || '';

  const [year, month, day] = date.split('-');

  const monthName = monthsInHebrew[parseInt(month, 10) - 1];
  const yearNumber = parseInt(year, 10);

  const [loading, setLoading] = useState(false);

  const [employee, setEmployee] = useState<any>(null);

  const [employees, setEmployees] = useState<any[]>([]);

  const [deposits, setDeposits] = useState<any[]>([]);

  const [activeDeposit, setActiveDeposit] = useState<any>(null);

  const activeContributions = useMemo(() => {
    if (activeDeposit && employees.length > 0 && employeeId) {
      const relevantEmployeeRecords = employees.filter(
        (emp) =>
          emp.employeeId === employeeId && emp.depositId === activeDeposit.id,
      );

      const combinedAllocations = relevantEmployeeRecords.flatMap(
        (emp) => emp.employeeContributionAllocation || [],
      );

      return combinedAllocations;
    }

    return [];
  }, [activeDeposit, employees, employeeId]);

  const totalActiveContributions = useMemo(() => {
    if (activeContributions.length > 0) {
      const total = activeContributions.reduce(
        (sum: any, contribution: any) =>
          sum + parseFloat(contribution.contributionAmount || 0),
        0,
      );

      return total;
    }

    return 0;
  }, [activeContributions]);

  useEffect(() => {
    const fetchEmployee = async () => {
      if (fileId && employeeId) {
        console.log('FILEID', fileId);
        console.log('EMPLOYEE', employeeId);
        setLoading(true);
        try {
          const response =
            await apiClient.models.Employee.listEmployeeByFileIdAndEmployeeId(
              { fileId: fileId, employeeId: { eq: employeeId } },
              {
                selectionSet: [
                  'id',
                  'employeeIdentifierType',
                  'employeeId',
                  'firstName',
                  'lastName',
                  'dateOfBirth',
                  'employeeIDatEmployer',
                  'employerCreatorIdentifier',
                  'city',
                  'street',
                  'houseNumber',
                  'apartmentNumber',
                  'postalCode',
                  'poBox',
                  'email',
                  'cellular',
                  'gender',
                  'employmentStartDate',
                  'salaryMonth',
                  'salary',
                  'kibbutzReturn',
                  'employeeDeclaration',
                  'section14',
                  'employeeContributionToFund',
                  'employeeContributionAllocation.*',
                  'userId',
                  'depositId',
                  'deposit.*',
                ],
              },
            );

          console.log('EMPLOYEE', response);

          setEmployees(response.data);

          if (response.data.length > 0) {
            setEmployee(response.data[0]);
          } else {
            setEmployee(null);
          }

          const potentiallyDuplicatedDeposits = response.data.flatMap(
            (employee: any) =>
              employee && employee.deposit ? [employee.deposit] : [],
          );

          const uniqueDepositsMap = new Map<string, Deposit>();
          potentiallyDuplicatedDeposits.forEach((deposit: Deposit) => {
            if (deposit && deposit.id) {
              uniqueDepositsMap.set(deposit.id, deposit);
            }
          });

          const uniqueDeposits = Array.from(uniqueDepositsMap.values());

          setDeposits(uniqueDeposits);

          if (uniqueDeposits.length > 0) {
            const linkedDeposit = uniqueDeposits.find((deposit) =>
              response.data.some(
                (emp: any) =>
                  emp.employeeId === employeeId && emp.depositId === deposit.id,
              ),
            );

            setActiveDeposit(linkedDeposit || uniqueDeposits[0]);
          } else {
            setActiveDeposit(null);
          }
        } catch (error) {
          console.error('Error fetching employee:', error);

          setEmployee(null);
          setEmployees([]);
          setDeposits([]);
          setActiveDeposit(null);
        } finally {
          setLoading(false);
        }
      } else {
        console.log('fileId or employeeId is missing.');

        setEmployee(null);
        setEmployees([]);
        setDeposits([]);
        setActiveDeposit(null);
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [fileId, employeeId]);

  useEffect(() => {
    if (deposits.length > 0) {
      const matchingDeposit = deposits.find((deposit) => {
        return employees.some(
          (emp) =>
            emp.employeeId === employeeId && emp.depositId === deposit.id,
        );
      });

      if (matchingDeposit) {
        setActiveDeposit(matchingDeposit);
      } else {
        setActiveDeposit(deposits[0]);
      }
    }
  }, [employeeId, deposits, employees]);

  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const totalEmployeeContributions = useMemo(() => {
    if (employees.length > 0 && deposits.length > 0) {
      let total = 0;
      deposits.forEach((deposit) => {
        const relevantEmployees = employees.filter(
          (emp) => emp.depositId === deposit.id,
        );
        relevantEmployees.forEach((emp) => {
          if (emp.employeeContributionAllocation) {
            emp.employeeContributionAllocation.forEach((contribution: any) => {
              total += parseFloat(
                contribution.contributionAmount ||
                  contribution.contributionRate ||
                  0,
              );
            });
          }
        });
      });

      return total;
    }

    return 0;
  }, [employees, deposits]);

  const handleCloseEmployeeSidebar = () => {
    setIsClosing(true);

    onClose();

    setTimeout(() => {
      setIsClosing(false);
    }, 500);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        handleCloseEmployeeSidebar();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const [showSalary, setShowSalary] = useState(false);

  const [depositsSelectOpen, setDepositsSelectOpen] = useState(false);
  const toggleDepositsSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setDepositsSelectOpen(!depositsSelectOpen);
  };

  const handleChangeActiveDeposit = (depositId: string) => {
    const selectedDeposit = deposits.find(
      (deposit) => deposit.id === depositId,
    );
    if (selectedDeposit) {
      setActiveDeposit(selectedDeposit);
      setDepositsSelectOpen(false);
    } else {
      console.warn(`Deposit with id ${depositId} not found.`);
    }
  };

  const depositsLength = deposits.length;

  if (employee) {
    return (
      <motion.div
        ref={sidebarRef}
        initial="hidden"
        animate={open && !isClosing ? 'visible' : 'hidden'}
        exit="hidden"
        variants={sidebarVariants}
        style={{
          width: '550px',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: '#FFF',
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            py: '45px',
            px: '25px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            overflowY: 'auto',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              boxShadow: '0px 0px 50px 0px #00000040',
              flexDirection: 'column',
              gap: '20px',
              py: '20px',
              px: '30px',
              borderRadius: '30px',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Icon
                style={{ cursor: 'pointer' }}
                icon={'material-symbols:close'}
                onClick={handleCloseEmployeeSidebar}
              />
            </Box>
            <EmployeeDetails
              employee={employee}
              depositsLength={depositsLength}
              totalContributions={totalEmployeeContributions}
            />
          </Box>
          <DepositsSelect
            deposits={deposits}
            activeDeposit={activeDeposit}
            handleChangeActiveDeposit={handleChangeActiveDeposit}
            totalActiveContributions={totalActiveContributions}
            activeContributions={activeContributions}
            depositsSelectOpen={depositsSelectOpen}
            toggleDepositsSelect={toggleDepositsSelect}
          />
          {employee && (
            <ContributionsTable contributions={activeContributions || []} />
          )}
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '23px',
                backgroundColor: '#9EA8B5',
                borderRadius: '10px',
                py: '10px',
                px: '20px',
                justifyContent: 'center',
              }}
            >
              <Typography
                fontWeight={'700'}
                fontSize={'12px'}
                textAlign={'right'}
                fontFamily={'Montserrat'}
                color={'#FFF'}
              >
                {employee.email}
              </Typography>
              <Typography
                fontWeight={'700'}
                fontSize={'12px'}
                textAlign={'right'}
                fontFamily={'Montserrat'}
                color={'#FFF'}
              >
                {employee.cellular}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flex: 1,
                backgroundColor: '#E7ECF259',
                borderRadius: '10px',
                py: '10px',
                px: '15px',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '23px' }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'AlmoniDL'}
                    color={'#757D88'}
                  >
                    גיל
                  </Typography>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'Montserrat'}
                    color={'#757D88'}
                  >
                    {calculateAge(employee.dateOfBirth || '')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'AlmoniDL'}
                    color={'#757D88'}
                  >
                    מגדר
                  </Typography>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'AlmoniDL'}
                    color={'#757D88'}
                  >
                    {parseInt(employee.gender || '') === 1 ? 'זכר' : 'נקבה'}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '23px' }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'AlmoniDL'}
                    color={'#757D88'}
                  >
                    תאריך לידה
                  </Typography>
                  <Typography
                    textAlign={'right'}
                    fontWeight={'700'}
                    fontSize={'14px'}
                    fontFamily={'Montserrat'}
                    color={'#757D88'}
                  >
                    {employee?.dateOfBirth
                      ? format(parseISO(employee.dateOfBirth), 'dd.MM.yyyy')
                      : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'AlmoniDL'}
                    color={'#757D88'}
                  >
                    כתובת
                  </Typography>
                  <Typography
                    fontWeight={'700'}
                    fontSize={'14px'}
                    textAlign={'right'}
                    fontFamily={'AlmoniDL'}
                    color={'#757D88'}
                    dir="rtl"
                  >
                    {employee.street} {employee.houseNumber}, {employee.city}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>
    );
  } else {
    return <Box></Box>;
  }
};

export default EmployeeSidebar;

const EmployeeDetails = ({
  employee,
  totalContributions,
  depositsLength,
}: any) => {
  const [showSalary, setShowSalary] = useState(false);

  const toggleShowSalary = () => {
    setShowSalary(!showSalary);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            py: '10px',
            px: '20px',
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'flex-end',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <img alt="coin" src="/assets/file/employees/sidebar/coin.svg" />
          <Stack>
            <Typography
              fontWeight={'700'}
              fontSize={'12px'}
              textAlign={'right'}
              fontFamily={'AlmoniDL'}
            >
              סך הכל
            </Typography>
            <Typography
              fontWeight={'800'}
              fontSize={'24px'}
              fontFamily={'Montserrat'}
            >
              {formatMoney(totalContributions || 0)}
            </Typography>
          </Stack>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '50%',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                fontWeight={'700'}
                fontSize={'18px'}
                textAlign={'right'}
                fontFamily={'AlmoniDL'}
              >
                {' '}
                {employee?.firstName} {employee?.lastName}
              </Typography>
              <Typography
                textAlign={'right'}
                fontWeight={'700'}
                fontSize={'16px'}
                fontFamily={'Montserrat'}
              >
                {employee?.employeeId}
              </Typography>
            </Box>
            <img alt="card" src="/assets/file/employees/sidebar/card.svg" />
          </Box>
        </Box>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            py: '5px',
            px: '15px',
            backgroundColor: '#F2F4F7',
            borderRadius: '30px',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            textAlign={'right'}
            fontFamily={'AlmoniDL'}
          >
            שכיר
          </Typography>
        </Box>
        <Box
          sx={{
            py: '5px',
            px: '15px',
            backgroundColor: '#F2F4F7',
            borderRadius: '30px',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            textAlign={'right'}
            fontFamily={'AlmoniDL'}
          >
            {' '}
            100% משרה
          </Typography>
        </Box>
        <Box
          sx={{
            py: '5px',
            px: '15px',
            backgroundColor: employee.section14 ? '#F2F4F7' : '#EEEEEE',
            borderRadius: '30px',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            textAlign={'right'}
            fontFamily={'AlmoniDL'}
            sx={{
              color: employee.section14 ? 'inherit' : '#BBBBBB',
            }}
          >
            סעיף 14
          </Typography>
        </Box>
        <Box
          sx={{
            py: '5px',
            px: '15px',
            backgroundColor: '#F2F4F7',
            borderRadius: '30px',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              textAlign={'right'}
              fontFamily={'AlmoniDL'}
            >
              {showSalary ? formatMoney(employee?.salary) : 'שכר יסוד'}
            </Typography>
            <Icon
              icon={showSalary ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
              onClick={toggleShowSalary}
              style={{ cursor: 'pointer' }}
            />
          </Stack>
        </Box>
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack spacing="5px" direction="row-reverse">
          <Typography
            sx={{ whiteSpace: 'nowrap' }}
            fontWeight={'700'}
            fontSize={'12px'}
            fontFamily={'AlmoniDL'}
          >
            תחילת עבודה
          </Typography>
          <Typography
            fontWeight={'700'}
            fontSize={'12px'}
            fontFamily={'Montserrat'}
          >
            {employee?.employmentStartDate
              ? format(parseISO(employee.employmentStartDate), 'dd.MM.yyyy')
              : ''}
          </Typography>
        </Stack>
        <Box
          sx={{
            py: '5px',
            px: '15px',
            backgroundColor: '#C1FE40',
            borderRadius: '30px',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              textAlign={'right'}
              fontFamily={'AlmoniDL'}
              dir="rtl"
            >
              {(() => {
                if (depositsLength === 1) {
                  return 'קופה 1';
                } else {
                  return `${depositsLength} קופות `;
                }
              })()}
            </Typography>
            <Icon icon="carbon:choices" />
          </Stack>
        </Box>
        <Box
          sx={{
            py: '5px',
            px: '15px',
            backgroundColor: '#C1FE40',
            borderRadius: '30px',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              textAlign={'right'}
              fontFamily={'AlmoniDL'}
            >
              {formatSalaryMonth(employee.salaryMonth)}
            </Typography>
            <Icon icon="cuida:calendar-outline" />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const DepositsSelect = ({
  deposits,
  activeDeposit,
  handleChangeActiveDeposit,
  totalActiveContributions,
  activeContributions,
  depositsSelectOpen,
  toggleDepositsSelect,
}: any) => {
  const openPopoverButtonRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          boxShadow: '0px 0px 50px 0px #00000040',
          flexDirection: 'column',
          gap: '20px',
          py: '20px',
          px: '30px',
          borderRadius: '30px',
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Box
            sx={{
              py: '10px',
              px: '20px',
              backgroundColor: '#F2F4F7',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'row-reverse',
              alignItems: 'center',
              gap: '14px',
            }}
          >
            <img alt="coin" src="/assets/file/employees/sidebar/coin.svg" />
            <Stack>
              <Typography
                fontWeight={'800'}
                fontSize={'24px'}
                fontFamily={'Montserrat'}
              >
                {formatMoney(totalActiveContributions || 0)}
              </Typography>
            </Stack>
          </Box>
          <Stack spacing={0.5} direction="row" alignItems="center">
            <Stack spacing={1} direction="row" alignItems="center">
              <Stack>
                <Typography
                  textAlign={'right'}
                  fontWeight={'700'}
                  fontSize={'16px'}
                  fontFamily={'AlmoniDL'}
                >
                  {activeDeposit?.fundNameAtEmployer}
                </Typography>
                <Typography
                  textAlign={'right'}
                  fontWeight={'700'}
                  fontSize={'16px'}
                  fontFamily={'AlmoniDL'}
                >
                  {extractFundNumber(activeDeposit?.fundIdentifier) === 0
                    ? activeDeposit?.companyId
                    : extractFundNumber(activeDeposit?.fundIdentifier)}
                </Typography>
              </Stack>
              <FundLogo
                width="100px"
                height="50px"
                fundNumber={activeDeposit?.fundIdentifier}
              />
            </Stack>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: '#F2F4F7',
                cursor: 'pointer',
              }}
              ref={openPopoverButtonRef}
              onClick={(e) => toggleDepositsSelect(e)}
            >
              <Icon
                icon="mdi:chevron-down"
                style={{
                  transition: 'transform 0.3s ease',
                  transform: depositsSelectOpen
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                }}
                width={24}
                height={24}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>
      <DepositsSelectPopover
        deposits={deposits}
        activeDeposit={activeDeposit}
        handleChangeActiveDeposit={handleChangeActiveDeposit}
        depositsSelectPopoverOpen={depositsSelectOpen}
        toggleDepositsSelectPopover={toggleDepositsSelect}
        anchorEl={openPopoverButtonRef.current}
      />
    </>
  );
};

const DepositsSelectPopover = ({
  deposits,
  activeDeposit,
  handleChangeActiveDeposit,
  depositsSelectPopoverOpen,
  toggleDepositsSelectPopover,
  anchorEl,
}: any) => {
  const popoverPosition = {
    top: anchorEl ? anchorEl.getBoundingClientRect().bottom + 50 : 0,
    right: 25,
  };

  console.log('Deposits', deposits);

  return (
    <Box
      sx={{
        position: 'absolute',
        ...popoverPosition,
        transformOrigin: 'top right',
        boxShadow: '0px 0px 50px 0px #00000040',
        backgroundColor: '#FFF',
        borderRadius: '30px',
        p: '15px',
        minWidth: '100px',
        zIndex: 1300,
        display: depositsSelectPopoverOpen ? 'block' : 'none',
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Stack spacing={1}>
        {deposits.map((deposit: any) => (
          <DepositsSelectPopoverItem
            key={deposit.fundIdentifier}
            deposit={deposit}
            activeDeposit={activeDeposit}
            handleChangeActiveDeposit={handleChangeActiveDeposit}
          />
        ))}
      </Stack>
    </Box>
  );
};

const DepositsSelectPopoverItem = ({
  deposit,
  activeDeposit,
  handleChangeActiveDeposit,
}: any) => {
  const isActive = activeDeposit && activeDeposit.id === deposit.id;

  return (
    <Box
      sx={{
        borderRadius: '20px',
        pt: '5px',
        px: '10px',
        backgroundColor: isActive ? '#F2F4F7' : 'transparent',
        '&:hover': {
          backgroundColor: '#F2F4F7',
        },
        cursor: 'pointer',
      }}
      onClick={() => {
        handleChangeActiveDeposit(deposit.id);
      }}
    >
      <Stack
        spacing={1}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Stack>
          <Typography
            textAlign={'right'}
            fontWeight={'700'}
            fontSize={'16px'}
            fontFamily={'AlmoniDL'}
          >
            {deposit?.fundNameAtEmployer}
          </Typography>
          <Typography
            textAlign={'right'}
            fontWeight={'700'}
            fontSize={'16px'}
            fontFamily={'AlmoniDL'}
          >
            {extractFundNumber(deposit?.fundIdentifier) === 0
              ? deposit?.companyId
              : extractFundNumber(deposit?.fundIdentifier)}
          </Typography>
        </Stack>
        <FundLogo
          width="100px"
          height="50px"
          fundNumber={deposit?.fundIdentifier}
        />
      </Stack>
    </Box>
  );
};
